import Polyglot from "node-polyglot";

import langFr from "@/locales/fr.json";
import langEn from "@/locales/en.json";
import langSp from "@/locales/es.json";
import langPt from "@/locales/pt.json";
import langIt from "@/locales/it.json";
import langDe from "@/locales/de.json";
import langAr from "@/locales/ar.json";
import langRu from "@/locales/ru.json";
import langNl from "@/locales/nl.json";
import SettingStorage, { storageLabels } from '@/utils/settings.storage';

const LangKey = {
  English: "en",
  French: "fr",
  Spanish: "es",
  Portuguese: "pt",
  Italian: "it",
  German: "de",
  Arabic: "ar",
  Russian: "ru",
  Dutch: "nl",
};

export const StringKey = {
  Playlist: "playlist",
  Minute: "minute",
  Minutes: "minutes",
  MinuteShort: "minute_short",
  MinutesShort: "minute_short",
  AgeGateWelcome: "age_gate_welcome",
  AgeGateWelcomeDesc: "age_gate_welcome_desc",
  InfoNotSaved: "info_not_saved",
  Settings: "settings",
  SettingsViewAccount: "settings_view_account",
  registerforFree: "register_for_free_tv",
  SettingsRegister: "settings_register",
  SettingsToLogin: "settings_to_login",
  SettingsAge: "settings_age",
  SettingsTimeLimit: "settings_time_limit",
  SettingsContentType: "settings_content_type",
  SettingsLanguage: "settings_language",
  SettingsMisc: "settings_misc",
  SettingsNoLimit: "settings_no_limit",
  SettingsContentTypeMix: "settings_content_type_mix",
  SettingsContentTypeMusic: "settings_content_type_music",
  SettingsContentTypeTopVideo: "search_category_top_videos",
  SettingsContentTypeFun: "settings_content_type_fun",
  SettingsContentTypeLearn: "settings_content_type_learn",
  SettingsLanguageFrench: "settings_language_french",
  SettingsLanguageEnglish: "settings_language_english",
  SettingsLanguageSpanish: "settings_language_spanish",
  SettingsLanguagePortuguese: "settings_language_portuguese",
  SettingsLanguageItalian: "settings_language_italian",
  SettingsLanguageGerman: "settings_language_german",
  SettingsLanguageRussian: "settings_language_russian",
  SettingsLanguageArabic: "settings_language_arabic",
  SettingsLanguageDutch: "settings_language_dutch",
  SettingsMiscMusicSound: "settings_misc_music_sound",
  SettingsMiscFullscreen: "settings_misc_fullscreen",
  SettingsMiscGcu: "settings_misc_gcu",
  SettingsMiscUnsubscribe: "settings_misc_unsubscribe",
  Unlock: "unlock",
  SessionExpired: "session_expired",
  SessionTouchScreen: "session_touch_screen",
  SessionParents: "session_parents",
  SessionExpiredNew: "session_expired_new",
  SessionTouchScreenNew: "session_touch_screen_new",
  UnsubscribeSuccess: "unsubscribe_success",
  UnsubscribeError: "unsubscribe_error",
  UnsubscribeAlreadyCancelled: "unsubscribe_already_cancelled",
  SubscriptionViewTitle: "subscription_view_title",
  SubscriptionViewSubtitle: "subscription_view_subtitle",
  SubscriptionViewButtonText: "subscription_view_button_text",
  SubscriptionViewRestore: "subscription_view_restore",
  CmpText: "cmp_text",
  CmpMore: "cmp_more",
  CmpAllow: "cmp_allow",
  CmpDeny: "cmp_deny",
  BannerTitle: "banner_title",
  BannerSubTitle: "subscription_header_line_1",
  BannerStartBtn: "banner_start_btn",
  BannerSubTitleMonth: "subscription_header_line_2",
  BannerSubInfo: "subscription_billing_info",
  Oops: "text_oops",
  ErrorMessage: "video_selection_toast_too_young",
  ConfirmationOk: "confirmation_ok",
  ServerErrorMessage: "popup_server_error",
  textKidAge: "text_kid_age"
};

class LangUtil {
  polyglotFr;
  polyglotEn;
  polyglotSp;
  polyglotPt;
  polyglotIt;
  polyglotDe;
  polyglotAr;
  polyglotRu;
  polyglotNl;

  constructor() {
    Object.values(LangKey).forEach((lang) => {
      switch (lang) {
        case LangKey.French: {
          this.polyglotFr = new Polyglot({ locale: lang, phrases: langFr });
          break;
        }
        case LangKey.English: {
          this.polyglotEn = new Polyglot({ locale: lang, phrases: langEn });
          break;
        }
        case LangKey.Spanish: {
          this.polyglotSp = new Polyglot({ locale: lang, phrases: langSp });
          break;
        }
        case LangKey.Portuguese: {
          this.polyglotPt = new Polyglot({ locale: lang, phrases: langPt });
          break;
        }
        case LangKey.Italian: {
          this.polyglotIt = new Polyglot({ locale: lang, phrases: langIt });
          break;
        }
        case LangKey.German: {
          this.polyglotDe = new Polyglot({ locale: lang, phrases: langDe });
          break;
        }
        case LangKey.Arabic: {
          this.polyglotAr = new Polyglot({ locale: lang, phrases: langAr });
          break;
        }
        case LangKey.Russian: {
          this.polyglotRu = new Polyglot({ locale: lang, phrases: langRu });
          break;
        }
        case LangKey.Dutch: {
          this.polyglotNl = new Polyglot({ locale: lang, phrases: langNl });
          break;
        }
      }
    });
  }

  getLang(language = null) {
    const userLang = navigator.language
      ? navigator.language.split("-")[0]
      : null;
    switch (language || userLang) {
      case LangKey.French: {
        return this.polyglotFr;
      }
      case LangKey.English: {
        return this.polyglotEn;
      }
      case LangKey.Spanish: {
        return this.polyglotSp;
      }
      case LangKey.Portuguese: {
        return this.polyglotPt;
      }
      case LangKey.Italian: {
        return this.polyglotIt;
      }
      case LangKey.German: {
        return this.polyglotDe;
      }
      case LangKey.Arabic: {
        return this.polyglotAr;
      }
      case LangKey.Russian: {
        return this.polyglotRu;
      }
      case LangKey.Dutch: {
        return this.polyglotNl;
      }
      default: {
        return this.polyglotFr;
      }
    }
  }

  getActiveLanguage(){
    return(navigator.language?navigator.language.split("-")[0]:null)||LangKey.English;
  }

  getSelectedLanguage() {
    let langauge = LangKey.English;
    const langCountryCode = SettingStorage.getOptionValue(storageLabels.language).codeLang;
    if(langCountryCode) {
      langauge = langCountryCode.split(',')[1];
    }
    return langauge;
  }

  mapLanguage(browserLang, apiLang) {
    const availableLanguages = Object.values(LangKey);

    if (availableLanguages.includes(browserLang)) {
      return browserLang;
    }

    return apiLang || LangKey.English;
  }
}

export default new LangUtil();
